/**
 * PlatformUpdatesTab — Admin UI para el feed "What's new" en /platform.
 *
 * Gestiona los PlatformUpdate que todos los users de cualquier tenant ven
 * al abrir el drawer de novedades (ícono 🎁 en el header).
 */
import { useState } from 'react'
import {
  Plus, Pencil, Trash2, Eye, EyeOff, Upload, Image as ImageIcon,
  Save, X, ExternalLink,
} from 'lucide-react'
import ReactMarkdown from 'react-markdown'
import {
  useAdminUpdates, useCreateUpdate, useUpdateUpdate, useDeleteUpdate,
  useUploadUpdateImage,
  CATEGORY_STYLES, SCOPE_FLAGS,
  type PlatformUpdate, type CreateUpdatePayload, type UpdateCategory, type UpdateScopeCode,
} from '@/modules/platform-updates/hooks/usePlatformUpdates'
import { ToastContainer, useToast } from '@/components/Toast'
import { ConfirmModal, useConfirmModal } from '@/components/ConfirmModal'
import { Button } from '@/components/Button'
import { Modal } from '@/components/ds/Modal'
import { inputCls, labelCls } from '@/lib/formStyles'
import { extractErrorMessage } from '@/lib/errorUtils'
import { EmptyState } from '@/components/EmptyState'
import { TextField } from '@/components/ds/TextField'
import { SelectField } from '@/components/ds/SelectField'

const CATEGORY_OPTIONS: { value: UpdateCategory; label: string }[] = [
  { value: 'feature', label: 'Nueva funcionalidad' },
  { value: 'improvement', label: 'Mejora' },
  { value: 'fix', label: 'Corrección' },
  { value: 'announcement', label: 'Anuncio' },
  { value: 'event', label: 'Evento' },
]

const SCOPE_OPTIONS: UpdateScopeCode[] = ['GLOBAL', 'CL', 'PE', 'AR', 'MX', 'EC']

function nowLocalIso(): string {
  const d = new Date()
  d.setMinutes(d.getMinutes() - d.getTimezoneOffset())
  return d.toISOString().slice(0, 16)
}

type Draft = Omit<Partial<CreateUpdatePayload>, 'scope'> & { scope: UpdateScopeCode[] }

const EMPTY_DRAFT: Draft = {
  title: '',
  body_markdown: '',
  image_url: '',
  video_embed_url: '',
  category: 'feature',
  scope: ['GLOBAL'],
  published_at: nowLocalIso(),
  is_active: true,
  action_url: '',
  action_label: '',
}

export function PlatformUpdatesTab() {
  const { data, isLoading } = useAdminUpdates()
  const updates = data?.updates ?? []
  const toast = useToast()
  const { confirm, modalProps } = useConfirmModal()
  const [editing, setEditing] = useState<PlatformUpdate | null>(null)
  const [creating, setCreating] = useState(false)
  const { mutate: deleteUpdate } = useDeleteUpdate()

  const handleDelete = (u: PlatformUpdate) => {
    confirm({
      message: `¿Eliminar "${u.title}"? Desaparece del feed de todos los tenants.`,
      variant: 'danger',
      onConfirm: () => {
        deleteUpdate(u.id, {
          onSuccess: () => toast.success('Update eliminado.'),
          onError: (e) => toast.error(extractErrorMessage(e, 'Error al eliminar')),
        })
      },
    })
  }

  const openEdit = (u: PlatformUpdate) => setEditing(u)
  const openCreate = () => setCreating(true)

  return (
    <>
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-content-muted">
          Gestiona el feed "What's new" visible desde el ícono 🎁 del header.
          Todos los tenants ven todos los updates; el scope se muestra como badge.
        </p>
        <Button onClick={openCreate} leftIcon={<Plus size={14} />}>Nueva actualización</Button>
      </div>

      {isLoading && (
        <p className="text-sm text-content-subtle py-8 text-center">Cargando...</p>
      )}

      <div className="space-y-2">
        {updates.map(u => {
          const cat = CATEGORY_STYLES[u.category]
          const dateStr = new Date(u.published_at).toLocaleString('es-CL', {
            day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
          })
          const isFuture = new Date(u.published_at) > new Date()
          return (
            <div key={u.id} className={`bg-surface border border-divider rounded-lg p-4 flex items-start gap-4 ${!u.is_active ? 'opacity-60' : ''}`}>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap mb-1">
                  <span className={`inline-flex items-center px-2 py-0.5 rounded-full border text-[11px] font-semibold ${cat.cls}`}>
                    {cat.label}
                  </span>
                  {u.scope.map(code => (
                    <span key={code} className="inline-flex items-center gap-1 text-[11px] text-content-muted bg-surface-muted rounded-full px-2 py-0.5">
                      {SCOPE_FLAGS[code]?.flag} {SCOPE_FLAGS[code]?.label}
                    </span>
                  ))}
                  <span className="text-[11px] text-content-subtle ml-auto">
                    {isFuture ? 'Programado para ' : ''}{dateStr}
                  </span>
                  {!u.is_active && (
                    <span className="inline-flex items-center gap-1 text-[11px] text-danger-content-soft">
                      <EyeOff size={11} /> Oculto
                    </span>
                  )}
                </div>
                <p className="font-medium text-content truncate">{u.title}</p>
                {u.body_markdown && (
                  <p className="text-xs text-content-muted truncate mt-0.5">
                    {u.body_markdown.replace(/[#*_`>]+/g, '').trim().slice(0, 120)}
                  </p>
                )}
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <button onClick={() => openEdit(u)}
                  className="p-1.5 rounded-lg text-content-muted hover:bg-soft" title="Editar">
                  <Pencil size={14} />
                </button>
                <button onClick={() => handleDelete(u)}
                  className="p-1.5 rounded-lg text-danger-content-soft hover:bg-danger-surface" title="Eliminar">
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
          )
        })}
        {!isLoading && updates.length === 0 && (
          <EmptyState
            icon={ImageIcon}
            title="Todavía no hay actualizaciones. Crea la primera con el botón de arriba."
            size="sm"
          />
        )}
      </div>

      {(creating || editing) && (
        <UpdateFormModal
          initial={editing}
          onClose={() => { setEditing(null); setCreating(false) }}
          toast={toast}
        />
      )}

      <ConfirmModal {...modalProps} />
      <ToastContainer toasts={toast.toasts} onDismiss={toast.dismiss} />
    </>
  )
}

// ─── Form modal ──────────────────────────────────────────────────────────────

function UpdateFormModal({
  initial, onClose, toast,
}: {
  initial: PlatformUpdate | null
  onClose: () => void
  toast: ReturnType<typeof useToast>
}) {
  const create = useCreateUpdate()
  const update = useUpdateUpdate(initial?.id ?? '')
  const upload = useUploadUpdateImage()

  const [draft, setDraft] = useState<Draft>(() => {
    if (!initial) return EMPTY_DRAFT
    return {
      title: initial.title,
      body_markdown: initial.body_markdown,
      image_url: initial.image_url,
      video_embed_url: initial.video_embed_url,
      category: initial.category,
      scope: initial.scope,
      published_at: new Date(initial.published_at).toISOString().slice(0, 16),
      is_active: initial.is_active ?? true,
      action_url: initial.action_url,
      action_label: initial.action_label,
    }
  })
  const [previewMd, setPreviewMd] = useState(false)

  const set = <K extends keyof Draft>(k: K, v: Draft[K]) =>
    setDraft(d => ({ ...d, [k]: v }))

  const toggleScope = (code: UpdateScopeCode) => {
    setDraft(d => {
      if (code === 'GLOBAL') return { ...d, scope: ['GLOBAL'] }
      const next = d.scope.filter(c => c !== 'GLOBAL')
      if (next.includes(code)) return { ...d, scope: next.filter(c => c !== code) }
      return { ...d, scope: [...next, code] }
    })
  }

  const handleUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    upload.mutate(file, {
      onSuccess: ({ url }) => set('image_url', url),
      onError: (err) => toast.error(extractErrorMessage(err, 'Error al subir imagen')),
    })
    e.target.value = ''
  }

  const handleSubmit = () => {
    if (!draft.title?.trim()) {
      toast.error('El título es obligatorio.')
      return
    }
    if (!draft.scope || draft.scope.length === 0) {
      toast.error('Seleccionar al menos un país o GLOBAL.')
      return
    }

    const payload: CreateUpdatePayload = {
      title: draft.title.trim(),
      body_markdown: draft.body_markdown ?? '',
      image_url: draft.image_url ?? '',
      video_embed_url: draft.video_embed_url ?? '',
      category: draft.category ?? 'feature',
      scope: draft.scope,
      published_at: new Date(draft.published_at ?? nowLocalIso()).toISOString(),
      is_active: draft.is_active ?? true,
      action_url: draft.action_url ?? '',
      action_label: draft.action_label ?? '',
    }

    const m = initial ? update : create
    m.mutate(payload as any, {
      onSuccess: () => {
        toast.success(initial ? 'Actualización editada.' : 'Actualización publicada.')
        onClose()
      },
      onError: (err) => toast.error(extractErrorMessage(err, 'Error al guardar')),
    })
  }

  const isPending = create.isPending || update.isPending

  return (
    <Modal open onClose={onClose} size="lg" title={initial ? 'Editar actualización' : 'Nueva actualización'}
      actions={<>
        <Button type="button" variant="secondary" onClick={onClose}>Cancelar</Button>
        <Button type="button" onClick={handleSubmit} isPending={isPending} leftIcon={<Save size={13} />}>
          {initial ? 'Guardar cambios' : 'Publicar'}
        </Button>
      </>}
    >
      <div className="space-y-4">
        <TextField
          dense
          label="Título *"
          value={draft.title ?? ''}
          onChange={e => set('title', e.target.value)}
          placeholder="Ej: Nuevo reporte de cobranza vencida"
          inputProps={{ type: 'text' }}
        />

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <SelectField
            dense
            label="Categoría *"
            value={draft.category ?? 'feature'}
            onChange={e => set('category', e.target.value as UpdateCategory)}
          >
            {CATEGORY_OPTIONS.map(o => (
            <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </SelectField>
          <TextField
            dense
            label="Publicar en (fecha y hora)"
            value={draft.published_at ?? ''}
            onChange={e => set('published_at', e.target.value)}
            inputProps={{ type: 'datetime-local' }}
          />
        </div>

        <div>
          <label className={labelCls}>Alcance *</label>
          <div className="flex flex-wrap gap-2">
            {SCOPE_OPTIONS.map(code => {
              const selected = draft.scope.includes(code)
              return (
                <button
                  key={code}
                  type="button"
                  onClick={() => toggleScope(code)}
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg border transition-colors ${
                    selected
                      ? 'border-primary bg-primary-50 text-primary'
                      : 'border-divider text-content-muted hover:bg-soft'
                  }`}
                >
                  {SCOPE_FLAGS[code].flag} {SCOPE_FLAGS[code].label}
                </button>
              )
            })}
          </div>
          <p className="text-xs text-content-subtle mt-1">
            "Global" es excluyente — se quita al elegir algún país específico.
          </p>
        </div>

        <div>
          <div className="flex items-center justify-between mb-1">
            <label className={labelCls + ' mb-0'}>Contenido (Markdown)</label>
            <button
              type="button"
              onClick={() => setPreviewMd(p => !p)}
              className="text-xs text-primary hover:underline inline-flex items-center gap-1"
            >
              {previewMd ? <><X size={11} /> Editar</> : <><Eye size={11} /> Vista previa</>}
            </button>
          </div>
          {previewMd ? (
            <div className="border border-divider rounded-lg p-3 min-h-[120px] bg-canvas">
              <ReactMarkdown>{draft.body_markdown || '_Escribe algo para ver la vista previa_'}</ReactMarkdown>
            </div>
          ) : (
            <textarea aria-label="Contenido (Markdown)"
              value={draft.body_markdown ?? ''}
              onChange={e => set('body_markdown', e.target.value)}
              rows={6}
              className={inputCls}
              placeholder="**Novedades del módulo de ventas**&#10;&#10;- Nuevo reporte de cobranza&#10;- Exportación a Excel"
            />
          )}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className={labelCls}>Imagen</label>
            <div className="flex items-center gap-2">
              <input aria-label="Imagen"
                type="url"
                value={draft.image_url ?? ''}
                onChange={e => set('image_url', e.target.value)}
                placeholder="URL o sube un archivo"
                className={inputCls + ' flex-1'}
              />
              <label className="inline-flex items-center gap-1.5 px-3 py-2 text-xs border border-divider rounded-lg cursor-pointer hover:bg-soft">
                {upload.isPending ? <span className="animate-pulse">Subiendo...</span> : <><Upload size={12} /> Subir</>}
                <input type="file" accept="image/*" className="hidden" onChange={handleUpload} disabled={upload.isPending} />
              </label>
            </div>
            {draft.image_url && (
              <img src={draft.image_url} alt="preview" className="mt-2 max-h-32 rounded-lg border border-divider" />
            )}
          </div>
          <TextField
            dense
            label="Video (YouTube / Loom)"
            value={draft.video_embed_url ?? ''}
            onChange={e => set('video_embed_url', e.target.value)}
            placeholder="https://youtu.be/... o https://www.loom.com/share/..."
            inputProps={{ type: 'url' }}
            hint="Si pegas watch/share URL, se convierte a embed automáticamente al guardar."
          />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <TextField
            dense
            label="URL de acción (opcional)"
            value={draft.action_url ?? ''}
            onChange={e => set('action_url', e.target.value)}
            placeholder="https://clobi.io/docs/..."
            inputProps={{ type: 'url' }}
          />
          <TextField
            dense
            label="Etiqueta del botón"
            value={draft.action_label ?? ''}
            onChange={e => set('action_label', e.target.value)}
            placeholder="Saber más"
            inputProps={{ type: 'text' }}
          />
        </div>

        <label className="flex items-center gap-2 text-sm cursor-pointer">
          <input
            type="checkbox"
            checked={draft.is_active ?? true}
            onChange={e => set('is_active', e.target.checked)}
            className="w-4 h-4 rounded border-divider-strong"
          />
          <span className="text-content">Activo (visible en el feed)</span>
        </label>
      </div>

    </Modal>
  )
}
